'use client';

import { type CSSProperties, type ReactNode, type RefObject, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useScrollLock } from '@akademi/ui';

// ---------------------------------------------------------------------------
// SbCommand — shadcn-compatible Command palette for site-builder blocks.
//
// Mirrors shadcn's Command + CommandDialog visual style via inline CSS +
// CSS variables, with no cmdk / Radix / Tailwind dependency so it renders
// in any host.
//
// Composable API:
//
//   // Full-screen modal variant
//   <SbCommandDialog open onClose={...}>
//     <SbCommandInput value={...} onChange={...} onKeyDown={...} />
//     <SbCommandTabBar tabs={...} activeTab={...} onTabChange={...} />
//     <SbCommandList>
//       <SbCommandEmpty>No results.</SbCommandEmpty>
//       <SbCommandGroup heading="Courses">
//         <SbCommandItem label="..." sublabel="..." thumbnail="..." onSelect={...} />
//       </SbCommandGroup>
//       <SbCommandSeparator />
//     </SbCommandList>
//   </SbCommandDialog>
//
//   // Inline (anchor-below dropdown variant) — wrap in a relative container
//   <SbCommand>
//     <SbCommandInput size="sm" ... />
//     {open && (
//       <SbCommandList style={{ position: 'absolute', top: 'calc(100% + 6px)', ... }}>
//         ...
//       </SbCommandList>
//     )}
//   </SbCommand>
// ---------------------------------------------------------------------------

// =============================================================================
// SbCommandDialog — portal + backdrop + centered panel
// =============================================================================

export interface SbCommandDialogProps {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
  /** Max width of the panel. Default 'min(760px, 100%)'. */
  maxWidth?: string;
}

export function SbCommandDialog({
  open,
  onClose,
  children,
  maxWidth = 'min(760px, 100%)',
}: SbCommandDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      const input = panelRef.current?.querySelector<HTMLElement>('input');
      input?.focus();
    }, 30);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      style={dialogOverlayStyle}
      role="presentation"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={panelRef} role="dialog" style={{ ...dialogPanelStyle, maxWidth }}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// =============================================================================
// SbCommand — inline wrapper (no portal, for dropdown usage)
// =============================================================================

export function SbCommand({ children, style }: { children?: ReactNode; style?: CSSProperties }) {
  return <div style={{ position: 'relative', ...style }}>{children}</div>;
}

// =============================================================================
// SbCommandInput — search bar (two sizes: 'lg' for dialog, 'sm' for inline)
// =============================================================================

export interface SbCommandInputProps {
  inputRef?: RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  /** 'lg' = overlay dialog (20px font, 20px icon, border-bottom separator).
   *  'sm' = inline pill (14px font, 16px icon, rounded border). Default 'lg'. */
  size?: 'lg' | 'sm';
  /** Inverse colour scheme (dark header). Only applies to 'sm'. */
  inverse?: boolean;
  /** Flush style (no border, just bottom rule). Only applies to 'sm'. */
  flush?: boolean;
  /** Stretch to fill parent width. */
  fillWidth?: boolean;
  /** Preferred pill width in px ('sm' only). Overrides the 220px default.
   *  A preference, not a floor — the pill still shrinks (min-width: 0)
   *  under a tight parent rather than overflowing it. Ignored when
   *  {@code fillWidth} is set. */
  width?: number;
  loading?: boolean;
  /** Show ×-clear button when there is a value. */
  onClear?: () => void;
  clearLabel?: string;
  /** Optional close button (dialog close). Shows only when provided. */
  onClose?: () => void;
  closeLabel?: string;
  ariaControls?: string;
  ariaExpanded?: boolean;
}

export function SbCommandInput({
  inputRef,
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  placeholder,
  size = 'lg',
  inverse = false,
  flush = false,
  fillWidth = false,
  width,
  loading = false,
  onClear,
  clearLabel,
  onClose,
  closeLabel,
  ariaControls,
  ariaExpanded,
}: SbCommandInputProps) {
  const { t } = useTranslation('site-builder-runtime');
  const inputPlaceholder = placeholder ?? t('command.searchPlaceholder');
  const clearText = clearLabel ?? t('actions.clear');
  const closeText = closeLabel ?? t('actions.close');
  const isLg = size === 'lg';

  const iconColor = isLg
    ? 'var(--color-text-secondary, #6b7280)'
    : inverse
      ? 'color-mix(in srgb, currentColor 70%, transparent)'
      : 'var(--color-text-secondary, #5f6675)';

  const inputColor = isLg
    ? 'var(--color-text-primary, #09090b)'
    : inverse
      ? 'currentColor'
      : 'var(--color-text-primary, #0f1218)';

  // `width` is a *preferred* size, not a floor: min-width: 0 + flex-shrink
  // let the pill actually shrink (rather than spilling over neighbouring
  // header content) once the row runs out of room — the container query
  // in portal.css (.lms-header-search-pill/-icon) swaps it for the icon
  // trigger well before it'd need to shrink to something unusably small.
  const sizeStyle: CSSProperties = fillWidth
    ? { width: '100%', minWidth: 0 }
    : { width: width ?? DEFAULT_SM_WIDTH, minWidth: 0, flexShrink: 1 };

  let wrapStyle: CSSProperties;
  if (isLg) {
    wrapStyle = inputWrapLgStyle;
  } else if (flush) {
    wrapStyle = {
      ...inputWrapSmBaseStyle,
      padding: '6px 0',
      borderRadius: 0,
      borderWidth: '0 0 1px 0',
      borderColor: inverse
        ? 'color-mix(in srgb, currentColor 22%, transparent)'
        : 'var(--color-neutral-200, #e5e7eb)',
      background: 'transparent',
      ...sizeStyle,
    };
  } else {
    // Prominent pill: sits on an *elevated* surface (distinct from both the
    // page and the header's card surface, so it never melts into the bar),
    // with a real border + soft shadow. Both the elevated surface and the
    // border token flip with the active theme, so light and dark both read
    // with strong contrast. Hover/focus emphasis is added via SB_CMD_INPUT_CSS.
    wrapStyle = {
      ...inputWrapSmBaseStyle,
      padding: '6px 16px',
      // Brand-accented pill: a teal (brand-600) border makes the bar stand
      // out as a real *colour*, not just a grey outline. brand-600 stays teal
      // in both themes (light #287f96, dark #46b0c4), so it reads clearly
      // against the near-black header in dark and the white surface in light.
      // A faint brand tint in the fill reinforces it. 1.5px keeps it crisp.
      borderWidth: inverse ? 1 : 1.5,
      borderColor: inverse
        ? 'var(--color-brand-600, #287f96)'
        : 'color-mix(in srgb, currentColor 32%, transparent)',
      background: inverse
        ? 'color-mix(in srgb, currentColor 12%, transparent)'
        : 'var(--color-surface-card, #fff)',
      boxShadow: inverse ? 'none' : 'var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.06))',
      ...sizeStyle,
    };
  }

  return (
    <div
      className="sb-cmd-input"
      data-sb-size={size}
      data-sb-inverse={inverse ? 'true' : undefined}
      data-sb-flush={flush ? 'true' : undefined}
      style={wrapStyle}
    >
      <style>{SB_CMD_INPUT_CSS}</style>
      {loading ? (
        <CmdSpinnerIcon size={isLg ? 20 : 16} color={iconColor} />
      ) : (
        <CmdSearchIcon size={isLg ? 20 : 16} color={iconColor} />
      )}
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={inputPlaceholder}
        style={{
          flex: 1,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: isLg ? 18 : 15,
          fontFamily: 'inherit',
          color: inputColor,
          marginLeft: 8,
          minWidth: 0,
        }}
      />
      {value && onClear && (
        <button
          type="button"
          aria-label={clearText}
          onClick={onClear}
          onMouseDown={(e) => e.preventDefault()}
          style={clearCloseBtnStyle}
        >
          {clearText}
        </button>
      )}
      {isLg && onClose && (
        <button
          type="button"
          aria-label={closeText}
          onClick={onClose}
          onMouseDown={(e) => e.preventDefault()}
          style={clearCloseBtnStyle}
        >
          {closeText}
        </button>
      )}
    </div>
  );
}

// =============================================================================
// SbCommandTabBar — horizontal tab strip (shown between input and list)
// =============================================================================

export interface SbCommandTab {
  id: string;
  label: string;
  count?: number;
}

export interface SbCommandTabBarProps {
  tabs: SbCommandTab[];
  activeTab: string;
  onTabChange: (id: string) => void;
}

export function SbCommandTabBar({ tabs, activeTab, onTabChange }: SbCommandTabBarProps) {
  if (tabs.length <= 1) return null;
  return (
    <div style={tabBarStyle}>
      {tabs.map((tab) => {
        const active = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onTabChange(tab.id)}
            style={{
              ...tabBtnBaseStyle,
              color: active
                ? 'var(--color-brand-500, #3b5bdb)'
                : 'var(--color-text-secondary, #5f6675)',
              fontWeight: active ? 600 : 400,
              borderBottomColor: active ? 'var(--color-brand-500, #3b5bdb)' : 'transparent',
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                style={{
                  marginLeft: 4,
                  fontSize: 11,
                  opacity: 0.65,
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// SbCommandList — scrollable results container
// =============================================================================

export function SbCommandList({
  children,
  style,
  id,
}: {
  children?: ReactNode;
  style?: CSSProperties;
  id?: string;
}) {
  return (
    <div id={id} style={{ ...listBaseStyle, ...style }}>
      {children}
    </div>
  );
}

// =============================================================================
// SbCommandEmpty — empty / loading state
// =============================================================================

export function SbCommandEmpty({ children }: { children?: ReactNode }) {
  return <div style={emptyStyle}>{children}</div>;
}

// =============================================================================
// SbCommandGroup — labeled section
// =============================================================================

export function SbCommandGroup({
  heading,
  children,
  separator = true,
}: {
  heading?: string;
  children?: ReactNode;
  /** Show a top-border separator above the group heading. Default true. */
  separator?: boolean;
}) {
  return (
    <div
      style={separator ? { borderTop: '1px solid var(--color-border-subtle, #f1f5f9)' } : undefined}
    >
      {heading && <div style={groupHeadingStyle}>{heading}</div>}
      {children}
    </div>
  );
}

// =============================================================================
// SbCommandItem — a single result row
// =============================================================================

export interface SbCommandItemProps {
  /** Image URL shown as thumbnail. */
  thumbnail?: string;
  /** Aspect of the thumbnail area: 'wide' (16:9-ish, 56×40) or 'square' (32×32). */
  thumbnailShape?: 'wide' | 'square';
  /** Fallback icon node shown when there is no thumbnail. */
  icon?: ReactNode;
  label: string;
  sublabel?: string;
  /** Keyboard-highlighted state. */
  active?: boolean;
  onSelect: () => void;
}

export function SbCommandItem({
  thumbnail,
  thumbnailShape = 'wide',
  icon,
  label,
  sublabel,
  active = false,
  onSelect,
}: SbCommandItemProps) {
  const isWide = thumbnailShape === 'wide';
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseDown={(e) => e.preventDefault()}
      style={{
        ...itemBaseStyle,
        background: active ? 'var(--color-surface-muted, #f4f4f5)' : 'transparent',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: isWide ? 56 : 32,
          height: 36,
          borderRadius: 6,
          background: thumbnail
            ? `url(${thumbnail}) center/cover no-repeat`
            : 'var(--color-surface-muted, #f1f5f9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-secondary, #6b7280)',
        }}
      >
        {!thumbnail && icon}
      </div>
      <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
        <div style={itemLabelStyle}>{label}</div>
        {sublabel && <div style={itemSublabelStyle}>{sublabel}</div>}
      </div>
    </button>
  );
}

// =============================================================================
// SbCommandSeparator
// =============================================================================

export function SbCommandSeparator({ style }: { style?: CSSProperties }) {
  return <div style={{ ...separatorStyle, ...style }} />;
}

// =============================================================================
// SbCommandSeeAll — "See all X →" link at the bottom of a group
// =============================================================================

export function SbCommandSeeAll({
  label,
  href,
  onNavigate,
}: {
  label: string;
  href: string;
  onNavigate: (href: string) => void;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 16px 10px' }}>
      <a
        href={href}
        onClick={(e) => {
          e.preventDefault();
          onNavigate(href);
        }}
        style={seeAllStyle}
      >
        {label} →
      </a>
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function CmdSearchIcon({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ display: 'inline-flex', flexShrink: 0 }}
    >
      <circle cx={11} cy={11} r={8} />
      <line x1={21} y1={21} x2={16.65} y2={16.65} />
    </svg>
  );
}

function CmdSpinnerIcon({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{
        display: 'inline-flex',
        flexShrink: 0,
        animation: 'sb-cmd-spin 0.8s linear infinite',
      }}
    >
      <style>{`@keyframes sb-cmd-spin{to{transform:rotate(360deg)}}`}</style>
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

// =============================================================================
// Styles
// =============================================================================

const dialogOverlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  background: 'rgba(0,0,0,0.55)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '8vh 16px 0',
};

const dialogPanelStyle: CSSProperties = {
  width: '100%',
  background: 'var(--color-surface-card, #fff)',
  borderRadius: 'var(--radius-xl, 16px)',
  boxShadow: '0 36px 72px rgba(0,0,0,0.22)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '80vh',
};

const inputWrapLgStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '18px 20px',
  borderBottom: '1px solid var(--color-border-subtle, #e5e7eb)',
  background: 'var(--color-surface-card, #fff)',
  gap: 0,
  flexShrink: 0,
};

// Interaction states for the inline pill. Inline styles can't express
// :hover / :focus-within / ::placeholder, so they live here and are scoped
// by the data-attributes stamped on the wrapper. Focus/hover rules use
// !important to win over the inline border/shadow set on the wrapper.
// Every colour is a theme-flipping token, so both light and dark read well.
const SB_CMD_INPUT_CSS = `
.sb-cmd-input{transition:border-color .15s ease,box-shadow .15s ease,background .15s ease;}
.sb-cmd-input[data-sb-size="sm"]:not([data-sb-flush]):not([data-sb-inverse]):hover{
  border-color:var(--color-gray-500,#319db3) !important;
}
.sb-cmd-input[data-sb-size="sm"]:not([data-sb-flush]):not([data-sb-inverse]):focus-within{
  border-color:var(--color-brand-500,#319db3) !important;
  box-shadow:0 0 0 3px color-mix(in srgb, var(--color-brand-500,#319db3) 28%, transparent) !important;
}
.sb-cmd-input[data-sb-size="sm"][data-sb-inverse]:focus-within{
  border-color:color-mix(in srgb, currentColor 55%, transparent) !important;
}
.sb-cmd-input:not([data-sb-inverse]) input::placeholder{color:var(--color-text-secondary,#6b7280);opacity:1;}
.sb-cmd-input[data-sb-inverse] input::placeholder{color:color-mix(in srgb, currentColor 60%, transparent);opacity:1;}
`;

/** Default preferred pill width (px) when no {@code width} prop is given.
 *  Mirrors {@code SEARCH_WIDTH_PRESETS_PX.default} in portal/shellTypes. */
const DEFAULT_SM_WIDTH = 220;

const inputWrapSmBaseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '6px 12px',
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 'var(--radius-pill, 9999px)',
  gap: 0,
  boxSizing: 'border-box',
};

const clearCloseBtnStyle: CSSProperties = {
  border: '1px solid var(--color-border-subtle, #e5e7eb)',
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--color-text-secondary, #6b7280)',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: 'inherit',
  padding: '3px 10px',
  borderRadius: 'var(--radius-sm, 4px)',
  marginLeft: 8,
  flexShrink: 0,
  lineHeight: '18px',
};

const tabBarStyle: CSSProperties = {
  display: 'flex',
  gap: 4,
  padding: '5px 16px',
  borderBottom: '1px solid var(--color-border-subtle, #e5e7eb)',
  overflowX: 'auto',
  flexShrink: 0,
};

const tabBtnBaseStyle: CSSProperties = {
  border: 'none',
  borderBottom: '2px solid transparent',
  background: 'none',
  cursor: 'pointer',
  padding: '9px 6px',
  fontSize: 13,
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
  transition: 'color 0.15s, border-color 0.15s',
  marginBottom: -1,
};

const listBaseStyle: CSSProperties = {
  overflowY: 'auto',
  flex: 1,
};

const emptyStyle: CSSProperties = {
  padding: '32px 20px',
  textAlign: 'center',
  fontSize: 14,
  color: 'var(--color-text-secondary, #6b7280)',
};

const groupHeadingStyle: CSSProperties = {
  padding: '10px 16px 4px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--font-tracking-wider, 0.06em)',
  textTransform: 'uppercase',
  color: 'var(--color-text-tertiary, #9ca3af)',
};

const itemBaseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '8px 16px',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.1s',
};

const itemLabelStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--color-text-primary, #09090b)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const itemSublabelStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--color-text-secondary, #6b7280)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginTop: 1,
};

const separatorStyle: CSSProperties = {
  height: 1,
  background: 'var(--color-border-subtle, #f1f5f9)',
  margin: '4px 0',
};

const seeAllStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--color-brand-500, #3b5bdb)',
  textDecoration: 'none',
};
